import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { Fragment, createRef, useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormInputFile } from "../../../components/FormInputFile";
import ImagePDFComponent from "../../../components/ImagePDFComponent";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { AlertService } from "../../../utility/service/alert-service";
import { handleFileDownloadRes } from "../../../utility/service/api-base";
import { deleteMultipartFile, downloadMultipartFile, uploadMultipartFile } from "../../../utility/service/api-file";
import { getBoudaryDeviceDropDownList } from "../../../utility/service/boundaryDevice-service";
import {
  CreateIdentifyInfo,
  checkImportantIndetificationItem,
  deleteIdentifyItem,
  downloadCarbonTemplateExcel,
  getCompanyPlaceList,
  getPlanOrganizationalBoundaryList,
  importCarbonTemplateExcel,
  queryPlanClassList,
  sortIdentificationItem,
  updateIdentifyInfo,
} from "../../../utility/service/carbon-service";
import { checkDpSuccess, splitFileExtension, splitFileName } from "../../../utility/service/tool-service";
import AddItem from "../components/AddItem";
import CategoryList from "./CategoryList";

const Identification = () => {
  const { t } = useTranslation();
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const [data, setData] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const { itemObj, setAction, action, setItemObj } = useOutletContext();
  const fileInputRef = useRef(null);
  const dataReordered = useRef([]);
  const sortableElement = useRef([]);
  const [tableOpen, setTableOpen] = useState({});
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [actionStatus, setActionStatus] = useState("add"); //目前action 狀態
  const [editObj, setEditObj] = useState({});
  const [previewDialog, setPreviewDialog] = useState(false);
  const [companyPlaceList, setCompanyPlaceList] = useState([]);
  const [boudaryDeviceList, setBoudaryDeviceList] = useState([]);
  //鎖住按鈕 存檔時
  const [confirmLoad, setConfirmLoad] = useState(false);
  const customFileInputRef = useRef(null);
  const [importantList, setImportantList] = useState([]);

  // 匯入檔案
  const menu = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [importFileInfo, setImportFileInfo] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const importfileInputRef = useRef(null);
  const [importPopup, setImportPopup] = useState(false);

  // 批次刪除
  const [batchAction, setBatchAction] = useState("");
  const [selectValue, setSelectValue] = useState({});
  const [batchDelPopup, setBatchDelPopup] = useState(false);

  // 排序
  const [reorderedDataMap, setReorderedDataMap] = useState({});
  // 簽核標記
  const [approveFlag, setApproveFlag] = useState(false);

  // 依盤查規範 (criterion) 決定後端排放源鑑別 API 所需的 class 參數
  // 規則：
  // - criterion === "0" → "S"（原本邏輯）
  // - criterion === "9" → "G"（GHG Protocol）
  // - 其他       → "C"
  const getClassByCriterion = (criterion) => {
    if (criterion === "0") return "S";
    if (criterion === "9") return "G";
    return "C";
  };

  // ==============================
  // G 模式：classNo=103 需要依 typeSeq 對應回 C 的 classNo（3/4/5/6）
  // 若對應到 C classNo=4/5/6 => 這筆要走「碳足跡係數 / 自定義係數」
  // 若對應到 C classNo=3 => 這筆要走「排放源係數」
  // ==============================
  const ghgTypeSeqToCTypeSeqMap = {
    1001: 1,
    1002: 2,
    1003: 3,
    1004: 4,
    1005: 5,
    1006: 8,
    1007: 9,
    1008: 6,
    1009: 10,
    1010: 5,
    1011: 9,
    1012: 32,
    2001: 11,
    2002: 12,
    3001: 22,
    3002: 23,
    3003: 24,
    3004: 16,
    3005: 26,
    3006: 20,
    3007: 18,
    3008: 25,
    3009: 17,
    3010: 28,
    3011: 29,
    3012: 30,
    3013: 32,
    3014: 31,
    3015: 31,
  };

  // RandomSeqLongUtil.toHexString 編碼字串（如 0_00000bb9）還原成 long
  const decodeRandomSeqLongToLongValue = (hexValue) => {
    if (hexValue == null || hexValue === "") return null;
    if (typeof hexValue === "number") return hexValue;
    const s = String(hexValue);
    if (!s.includes("_")) {
      const n = Number(s);
      return Number.isNaN(n) ? null : n;
    }
    const arr = s.split("_");
    if (arr.length < 2) return null;
    let dateHex = arr[0];
    const timeHex = arr[1];
    // 對齊 RandomSeqLongUtil.toLongValue：根據 timeHex 長度補齊日期部分的尾碼
    if (timeHex.length === 10) {
      dateHex = dateHex + "0000000000";
    } else {
      dateHex = dateHex + "00000000";
    }
    const longDate = parseInt(dateHex, 16);
    const longTime = parseInt(timeHex, 16);
    if (Number.isNaN(longDate) || Number.isNaN(longTime)) return null;
    return longDate + longTime;
  };

  const parseTypeSeqLong = (value) => decodeRandomSeqLongToLongValue(value);

  const getMappedCClassNoWhenGhgClassNo103 = () => {
    const classNo = getValues("classSeq")?.classNo;
    if (classNo !== "103") return null;

    const selectedTypeSeq =
      getValues("typeSeq")?.subTypeList?.length > 0 ? getValues("subType")?.typeSeq : getValues("typeSeq")?.typeSeq;
    const ghgTypeSeq = parseTypeSeqLong(selectedTypeSeq);
    if (ghgTypeSeq == null || Number.isNaN(ghgTypeSeq)) return 3;

    const cTypeSeq = ghgTypeSeqToCTypeSeqMap[ghgTypeSeq] ?? ghgTypeSeq;
    if (cTypeSeq >= 16 && cTypeSeq <= 21) return 3;
    if (cTypeSeq >= 22 && cTypeSeq <= 26) return 4;
    if (cTypeSeq >= 28 && cTypeSeq <= 31) return 5;
    if (cTypeSeq === 32) return 6;
    return 3;
  };

  const getIsCarbonCoefficientMode = () => {
    const tempClassNo = getValues("classSeq")?.classNo;
    if (tempClassNo === "4" || tempClassNo === "5" || tempClassNo === "6") return true;
    if (tempClassNo === "103") {
      const mappedCClassNo = getMappedCClassNoWhenGhgClassNo103();
      return mappedCClassNo === 4 || mappedCClassNo === 5 || mappedCClassNo === 6;
    }
    return false;
  };

  const addItem = () => {
    setAddDialog(true);
    setActionStatus("add");
    setValue("isCalculate", true);
    if (companyPlaceList?.length === 1) {
      setValue("planPlaceSeq", companyPlaceList[0]?.planPlaceSeq, { shouldValidate: true, shouldDirty: true });
    }
    // v0.75
    setValue("isBiomass", false);
    setValue("isCHP", false);
    clearErrors(["deviceCode", "quantity", "remark", "materialCode"]);
  };

  const axios_queryPlanClassList = (
    isModify = "",
    reqBody = {
      planSn: itemObj?.planSn,
      class: getClassByCriterion(itemObj?.criterion),
    },
  ) => {
    //如果 isModify === 'edit' 表示修改過 且要判斷是否是重大性鑑別 importanceIdentification 為 Y 就要call 307
    //查詢排放源鑑別清單
    checkLoading(loading);
    queryPlanClassList(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          setApproveFlag(res?.data?.RespBody?.approveFlag);
          setData(res?.data.RespBody?.classList);
          setTableOpen(() => {
            const initialOpen = {};
            res?.data.RespBody?.classList?.forEach((item) => {
              initialOpen[item?.className] = true;
            });
            return initialOpen;
          });
          if (!sortableElement.current.length) {
            sortableElement.current = Array(res?.data.RespBody?.classList?.length)
              .fill(null)
              .map((_, i) => sortableElement.current[i] || createRef());
          }
          dataReordered.current = [];
          if (!dataReordered?.current?.length) {
            dataReordered.current = Array(res?.data.RespBody?.classList?.length)
              .fill(null)
              .map((_, i) => dataReordered.current[i] || createRef());
          }
          res?.data?.RespBody?.classList?.map((item, index) => {
            dataReordered.current[index] = item?.emissionList;
            // console.log("dataReordered.current[index]",index,dataReordered.current[index]);
          });
          if (isModify === "edit" && itemObj?.importanceIdentification === "Y") {
            axios_checkImportantIndetificationItem();
          }
        }
      })
      .finally(() => checkLoading(loading, false));
  };

  const axios_checkImportantIndetificationItem = () => {
    checkImportantIndetificationItem({ planSn: itemObj?.planSn }).then((res) => {
      if (checkDpSuccess(res)) {
        let result = res?.data?.RespBody?.importanceList;
        if (result?.length > 0) {
          let typeNameStr = result.map((item) => item.typeName).join("\n");
          setImportantList(typeNameStr);
        } else if (result?.length === 0) {
          setImportantList([]);
        }
      }
    });
  };

  useEffect(() => {
    axios_queryPlanClassList();
    axios_getPlanOrganizationalBoundaryList();
    axios_getBoudaryDeviceDropDownList();
    if (itemObj?.importanceIdentification === "Y") {
      axios_checkImportantIndetificationItem();
    }
  }, []);

  const items = [
    {
      label: t("button.downloadCarbonFootprintTemplate"),
      icon: "pi pi-download",
      disabled: !isEditable,
      command: () => {
        downloadCarbonTemplate();
      },
    },
    {
      label: t("button.importCarbonFootprintTemplate"),
      icon: "pi pi-upload",
      disabled: !isEditable,
      command: () => {
        setImportPopup(true);
      },
    },
    {
      label: `${t("button.edit")}${t("button.sort")}`,
      icon: "pi pi-sort-alt",
      disabled: !isEditable,
      command: () => {
        setBatchAction("sort");
      },
    },
    {
      label: `${t("button.batch")}${t("button.delete")}`,
      icon: "pi pi-trash",
      disabled: !isEditable,
      command: () => {
        setBatchAction("delete");
      },
    },
    {
      label: `${t("button.batch")}${t("button.edit")}${t("attached_picture")}/${t("attachments")}`,
      icon: "pi pi-pencil",
      disabled: !isEditable,
      command: () => {
        setBatchAction("upload");
      },
    },
  ];

  // 組織邊界場所
  const axios_getPlanOrganizationalBoundaryList = () => {
    getPlanOrganizationalBoundaryList({ planSn: itemObj?.planSn }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.planList;

        // 找尋場所名稱
        getCompanyPlaceList().then((res) => {
          if (checkDpSuccess(res)) {
            let data2 = res.data.RespBody.companyPlaceList?.filter((item) => item.companySeq === itemObj?.companySeq)[0]
              ?.placeList;
            const placeMap = new Map();
            data2.forEach((item) => {
              placeMap.set(item.placeSeq, item.placeName);
            });
            data.forEach((plan) => {
              plan.placeName = placeMap.get(plan.placeSeq) || null;
            });
          }
        });

        setCompanyPlaceList(data);
      }
    });
  };

  // 邊界設備列表查詢
  const axios_getBoudaryDeviceDropDownList = () => {
    getBoudaryDeviceDropDownList({
      companySeq: itemObj?.companySeq,
    }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.placeDeviceList;
        setBoudaryDeviceList(data);
      }
    });
  };

  const {
    register,
    unregister,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    trigger,
    resetField,
    reset: resetForm,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  // const getFieldsWithErrors = () => { //驗證表單錯誤
  //     const fieldsWithErrors = Object.keys(errors);
  //     console.log("Fields with errors:", fieldsWithErrors,isValid,"cusSourceSeq===",getValues("cusSourceSeq"));
  //     trigger();
  //   };

  if (!data.length) {
    return <></>;
  }

  const isFieldEmpty = (value) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return value;
  };

  const confirmPreview = (delData = "N") => {
    setConfirmLoad(true);
    const fileExtension = isFieldEmpty(getValues("fileExtension"));
    const fileName = isFieldEmpty(getValues("fileName"));
    const devicePic = isFieldEmpty(getValues("devicePic"));
    const isCusCoefficent = getValues("isCusCoefficent");
    const customFile = isFieldEmpty(getValues("customFile"));
    const customFileName = isFieldEmpty(getValues("customFileName"));
    const customFileExtension = isFieldEmpty(getValues("customFileExtension"));
    const tempClassNo = getValues("classSeq")?.classNo;
    let sourceType = getIsCarbonCoefficientMode() ? "1" : "0";
    const reqBody = {
      processCode: getValues("processCode"),
      customFileExtension: customFileExtension,
      identifyDetailList: JSON.stringify(getValues("identifyDetailList")) || JSON.stringify([]),
      planPlaceSeq: getValues("planPlaceSeq"),
      placeDeviceSeq: getValues("placeDeviceSeq"),
      dept: getValues("dept"),
      location: getValues("location"),
      classSeq: getValues("classSeq")?.classSeq,
      sourceType: sourceType,
      typeSeq:
        getValues("typeSeq")?.subTypeList?.length > 0 ? getValues("subType")?.typeSeq : getValues("typeSeq")?.typeSeq,
      deviceCode: getValues("deviceCode"),
      quantity: getValues("quantity"),
      customerNo: getValues("customerNo"),
      isCalculate: getValues("isCalculate") === true ? "Y" : "N",
      remark: getValues("remark"),
      identifySeq: editObj?.identifySeq,
      delData: delData,
      sort: editObj?.sort,
      materialType: getValues("materialType"),
      materialCode: getValues("materialCode"),
      isBiomass: getValues("isBiomass") === true ? "Y" : "N",
      isCHP: getValues("isCHP") === true ? "Y" : "N",
      gwpVersion: itemObj.displayGwp,
    };

    const formData = new FormData();
    Object.entries(reqBody).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    // 排放源鑑別附檔判斷
    //有fileId 但沒有圖片表示要刪除 只傳 fileId 給後端 (刪除圖片)
    if (editObj?.fileId && devicePic === null) {
      formData.append("fileId", editObj?.fileId);
    } else if (editObj?.fileId === undefined && devicePic !== null) {
      //如果沒有fileId 但有file 表示要新增file
      formData.append("devicePic", devicePic);
      formData.append("fileName", fileName);
      formData.append("fileExtension", fileExtension);
    } else if (devicePic !== null && editObj?.fileName !== fileName) {
      //如果有圖片 但是檔名不同表示要更新圖片
      formData.append("fileId", editObj?.fileId);
      formData.append("devicePic", devicePic);
      formData.append("fileName", fileName);
      formData.append("fileExtension", fileExtension);
    }

    if (getIsCarbonCoefficientMode()) {
      // 碳足跡係數 / 自定義係數
      if (isCusCoefficent === true) {
        //自定義係數
        let cusSourceSeq = getValues("cusSourceSeq");
        formData.append(
          "sourceSeq",
          cusSourceSeq !== "" && cusSourceSeq !== undefined && cusSourceSeq !== null ? cusSourceSeq : "0",
        );
        let customRate1 = getValues("customRate1");
        let customRateSource = getValues("customRateSource");
        let customRateType = getValues("customRateType");
        let customEmission = getValues("customEmission");
        let customDesc = getValues("customDesc");
        if (customRate1) {
          formData.append("customRate1", customRate1);
        }
        if (customEmission) {
          formData.append("customEmission", customEmission);
        }
        formData.append("customRateSource", customRateSource);
        formData.append("customRateType", customRateType);
        formData.append("customDesc", customDesc);
        formData.append("hasCustom", "Y");
      } else {
        //碳足跡係數
        formData.append("sourceSeq", getValues("cusSourceSeq") || getValues("sourceSeq"));
        formData.append("hasCustom", "N");
      }
    } else {
      formData.append("sourceSeq", getValues("sourceSeq"));
    }

    if (getIsCarbonCoefficientMode() && isCusCoefficent) {
      //類別四、五、六 自定義係數如果有檔案走此判斷
      //有customFileId 但沒有圖片表示要刪除 只傳 customFileId 給後端 (刪除圖片)
      if (editObj?.customFileId && customFile === null) {
        formData.append("customFileId", editObj?.customFileId);
        // console.log("刪除圖片");
      } else if (editObj?.customFileId === null && customFile !== null) {
        //如果沒有customFileId 但有file 表示要新增file
        formData.append("customFile", customFile);
        formData.append("customFileName", customFileName);
        formData.append("customFileExtension", customFileExtension);
        // console.log("新增file");
      } else if (customFile !== null && editObj?.customFileName !== customFileName && editObj?.customFileId !== null) {
        //如果有圖片 但是檔名不同表示要更新圖片
        formData.append("customFileId", editObj?.customFileId);
        formData.append("customFile", customFile);
        formData.append("customFileName", customFileName);
        formData.append("customFileExtension", customFileExtension);
        // console.log("更新file");
      }
    }

    updateIdentifyInfo(formData)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleCloseAddDialog();
          setPreviewDialog(false);
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          axios_queryPlanClassList("edit");
          setConfirmLoad(false);
        }
      })
      .finally((fin) => {
        setConfirmLoad(false);
      });
  };

  const dialogSubmit = (delData = "N") => {
    setConfirmLoad(true);
    checkLoading(loading);

    const fileExtension = isFieldEmpty(getValues("fileExtension"));
    const fileName = isFieldEmpty(getValues("fileName"));
    const devicePic = isFieldEmpty(getValues("devicePic"));
    const isCusCoefficent = getValues("isCusCoefficent");
    const customFile = isFieldEmpty(getValues("customFile"));
    const customFileName = isFieldEmpty(getValues("customFileName"));
    const customFileExtension = isFieldEmpty(getValues("customFileExtension"));
    const tempClassNo = getValues("classSeq")?.classNo;
    let sourceType = getIsCarbonCoefficientMode() ? "1" : "0";

    const reqBody = {
      processCode: getValues("processCode"),
      deviceCode: getValues("deviceCode"),
      quantity: getValues("quantity"),
      customerNo: getValues("customerNo"),
      isCusCoefficent: isCusCoefficent,
      identifyDetailList: JSON.stringify(getValues("identifyDetailList")) || JSON.stringify([]),
      planPlaceSeq: getValues("planPlaceSeq"),
      placeDeviceSeq: getValues("placeDeviceSeq"),
      dept: getValues("dept"),
      location: getValues("location"),
      classSeq: getValues("classSeq")?.classSeq,
      sourceType: sourceType,
      typeSeq:
        getValues("typeSeq")?.subTypeList?.length > 0 ? getValues("subType")?.typeSeq : getValues("typeSeq")?.typeSeq,
      isCalculate: getValues("isCalculate") === true ? "Y" : "N",
      remark: getValues("remark"),
      materialType: getValues("materialType"),
      materialCode: getValues("materialCode"),
      isBiomass: getValues("isBiomass") === true ? "Y" : "N",
      isCHP: getValues("isCHP") === true ? "Y" : "N",
      gwpVersion: itemObj.displayGwp,
    };

    const formData = new FormData();
    Object.entries(reqBody).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    if (getIsCarbonCoefficientMode()) {
      // 碳足跡係數 / 自定義係數

      if (isCusCoefficent === true) {
        //自定義係數
        let cusSourceSeq = getValues("cusSourceSeq");
        formData.append(
          "sourceSeq",
          cusSourceSeq !== "" && cusSourceSeq !== undefined && cusSourceSeq !== null ? cusSourceSeq : "0",
        );
        let customRate1 = getValues("customRate1");
        let customRateSource = getValues("customRateSource");
        let customRateType = getValues("customRateType");
        let customEmission = getValues("customEmission");
        let customDesc = getValues("customDesc");
        if (customRate1) {
          formData.append("customRate1", customRate1);
        }
        if (customEmission) {
          formData.append("customEmission", customEmission);
        }
        formData.append("customRateSource", customRateSource);
        formData.append("customRateType", customRateType);
        formData.append("customDesc", customDesc);
        formData.append("hasCustom", "Y");
      } else {
        //碳足跡係數
        formData.append("sourceSeq", getValues("cusSourceSeq") || getValues("sourceSeq"));
        formData.append("hasCustom", "N");
      }
    } else {
      formData.append("sourceSeq", getValues("sourceSeq"));
    }

    //圖片相關參數
    //編輯狀態
    if (actionStatus === "edit") {
      // 排放源鑑別附檔判斷
      //有fileId 但沒有圖片表示要刪除 只傳 fileId 給後端 (刪除圖片)
      if (editObj?.fileId && devicePic === null) {
        formData.append("fileId", editObj?.fileId);
        // console.log("刪除圖片");
      } else if (editObj?.fileId === null && devicePic !== null) {
        //如果沒有fileId 但有file 表示要新增file
        formData.append("devicePic", devicePic);
        formData.append("fileName", fileName);
        formData.append("fileExtension", fileExtension);
        // console.log("新增file");
      } else if (devicePic !== null && editObj?.fileName !== fileName && editObj?.fileId !== null) {
        //如果有圖片 但是檔名不同表示要更新圖片
        formData.append("fileId", editObj?.fileId);
        formData.append("devicePic", devicePic);
        formData.append("fileName", fileName);
        formData.append("fileExtension", fileExtension);
        // console.log("更新file");
      }

      //類別四、五、六 自定義係數如果有檔案走此判斷
      if (getIsCarbonCoefficientMode() && isCusCoefficent) {
        //有customFileId 但沒有圖片表示要刪除 只傳 customFileId 給後端 (刪除圖片)
        if (editObj?.customFileId && customFile === null) {
          formData.append("customFileId", editObj?.customFileId);
          // console.log("刪除圖片");
        } else if (editObj?.customFileId === null && customFile !== null) {
          //如果沒有customFileId 但有file 表示要新增file
          formData.append("customFile", customFile);
          formData.append("customFileName", customFileName);
          formData.append("customFileExtension", customFileExtension);
          // console.log("新增file");
        } else if (
          customFile !== null &&
          editObj?.customFileName !== customFileName &&
          editObj?.customFileId !== null
        ) {
          //如果有圖片 但是檔名不同表示要更新圖片
          formData.append("customFileId", editObj?.customFileId);
          formData.append("customFile", customFile);
          formData.append("customFileName", customFileName);
          formData.append("customFileExtension", customFileExtension);
          // console.log("更新file");
        }
      }
    }

    if (actionStatus === "add" || actionStatus === "copy") {
      //新增狀態
      if (devicePic !== null) {
        formData.append("fileExtension", fileExtension);
        formData.append("fileName", fileName);
        formData.append("devicePic", devicePic);
      }
      if (getIsCarbonCoefficientMode() && isCusCoefficent && customFile !== null) {
        formData.append("customFileExtension", customFileExtension);
        formData.append("customFileName", customFileName);
        formData.append("customFile", customFile);
      }
    }

    if (actionStatus === "add" || actionStatus === "copy") {
      //新增/複製 排放源鑑別
      // reqBody.planSn = itemObj?.planSn
      formData.append("planSn", itemObj?.planSn);
      CreateIdentifyInfo(formData)
        .then((res) => {
          // console.log(res);
          if (checkDpSuccess(res)) {
            setConfirmLoad(false);
            setItemObj((pre) => {
              return {
                ...pre,
                identifySeq: res.data?.RespBody.identifySeq,
              };
            });

            handleCloseAddDialog();
            handleSuccessMessage(dispatch, t("button.add") + " " + t("message.success"));
            axios_queryPlanClassList("edit");
          }
        })
        .finally(() => {
          checkLoading(loading, false);
          setConfirmLoad(false);
        });
    } else if (actionStatus === "edit") {
      //更新排放源鑑別
      //如果有變動需要彈窗確認
      let cusSourceSeq = getValues("cusSourceSeq");
      let sourceSEQ =
        getIsCarbonCoefficientMode() && cusSourceSeq !== "" && cusSourceSeq !== undefined && cusSourceSeq !== null
          ? cusSourceSeq
          : getValues("sourceSeq");

      if (editObj?.deviceCode !== getValues("deviceCode") || editObj?.sourceSeq !== sourceSEQ) {
        setPreviewDialog(true);
        checkLoading(loading, false);
        setConfirmLoad(false);
      } else {
        // reqBody.identifySeq = editObj?.identifySeq
        // reqBody.delData = delData;
        // reqBody.sort = editObj?.sort;

        formData.append("identifySeq", editObj?.identifySeq);
        formData.append("delData", delData);
        formData.append("sort", editObj?.sort);

        updateIdentifyInfo(formData)
          .then((res) => {
            if (checkDpSuccess(res)) {
              setConfirmLoad(false);
              handleCloseAddDialog();
              setPreviewDialog(false);
              handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
              axios_queryPlanClassList("edit");
            }
          })
          .finally(() => {
            checkLoading(loading, false);
            setConfirmLoad(false);
          });
      }
    }

    // for (var [a, b] of formData.entries()) {
    //     console.log(a, b);
    //  }
    // console.log(reqBody);
  };

  const handleCloseAddDialog = () => {
    //每次點開新增視窗清除預設值
    setAddDialog(false);
    setValue("planPlaceSeq", "");
    setValue("quantity", "");
    setValue("dept", "");
    setValue("placeDeviceSeq", "");
    setValue("location", "");
    setValue("classSeq", "");
    setValue("typeSeq", "");
    setValue("subType", "");
    setValue("processCode", "");
    setValue("quantity", "");
    setValue("sourceSeq", "");
    setValue("deviceCode", "");
    setValue("remark", "");
    setValue("fileExtension", "");
    setValue("fileName", "");
    setValue("devicePic", "");
    setValue("newEqu", ""); //新增設備
    //v0.75新增
    setValue("materialType", "");
    setValue("materialCode", "");
    setValue("isBiomass", false);
    setValue("isCHP", false);
    setValue("customerNo", "");
    // 類四新增參數
    setValue("sourceName", "");
    setValue("cusSourceSeq", "");
    setValue("isCusCoefficent", false);
    setValue("customRate1", "");
    setValue("customRateSource", "");
    setValue("customRateType", "");
    setValue("customEmission", "");
    setValue("customDesc", "");
    setValue("customFile", "");
    setValue("customFileName", "");
    setValue("customFileExtension", "");
  };

  const getIdentifyDetailList = ({ classSeq, typeSeq, processCode, sourceSeq }) => {
    const targetDisplayClassObj = data?.find((item) => {
      return item?.displayClassNo === classSeq.classNo;
    })?.emissionList;

    if (!targetDisplayClassObj) return [];

    const targetIdentifyDetailList =
      targetDisplayClassObj?.find((item) => {
        return (
          // item?.classSeq === classSeq &&
          item?.typeSeq === typeSeq.typeSeq &&
          // item?.processCode === processCode &&
          item?.sourceSeq === sourceSeq
        );
      })?.identifyDetailList || [];

    return targetIdentifyDetailList;
  };

  const editItem = (rowData, classGroup, modifyAction = "edit") => {
    setAddDialog(true);
    setActionStatus(modifyAction);
    if ((modifyAction === "copy" || modifyAction === "edit" || modifyAction === "detail") && rowData?.fileId !== null) {
      //如果是copy 又有檔案 將檔案下載改成file存給devicePic
      downloadMultipartFile({
        fileId: rowData?.fileId,
      }).then((res) => {
        if (res) {
          const binaryData = res.data;
          const blob = new Blob([binaryData], { type: "application/octet-stream" });
          const file = new File([blob], `${rowData?.fileName}.${rowData?.fileExtension}`, {
            type: blob.type,
          });
          setValue("devicePic", file);
        }
      });
    }
    if (
      (modifyAction === "copy" || modifyAction === "edit" || modifyAction === "detail") &&
      rowData?.customFileId !== null
    ) {
      //如果是copy 又有檔案 將檔案下載改成file存給devicePic
      downloadMultipartFile({
        fileId: rowData?.customFileId,
      }).then((res) => {
        if (res) {
          const binaryData = res.data;
          const blob = new Blob([binaryData], { type: "application/octet-stream" });
          const file = new File([blob], `${rowData?.customFileName}.${rowData?.customFileExtension}`, {
            type: blob.type,
          });
          setValue("customFile", file, { shouldValidate: true });
          trigger("customFile");
          // 嘗試將文件設定到文件選擇框
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          if (customFileInputRef?.current) {
            customFileInputRef.current.files = dataTransfer.files;
          }
        }
      });
    }
    setEditObj({
      ...rowData,
      className: classGroup?.className,
      classNo: rowData?.classNo,
      classSeq: classGroup?.classSeq,
      typeSeq: rowData?.typeSeq,
      deviceCode: rowData?.deviceCode,
      sourceSeq: rowData?.sourceSeq,
      remark: rowData?.remark,
      quantity: rowData?.quantity,
      isCalculate: rowData?.isCalculate,
      processCode: rowData?.processCode,
      fileExtension: rowData?.fileExtension,
      fileName: rowData?.fileName,
      identifySeq: rowData?.identifySeq,
      sort: rowData?.sort,
      // devicePic:rowData?.devicePic,//之後須刪除改由下面三個顯示
      fileId: modifyAction === "copy" ? null : rowData?.fileId,
      filePathSml: rowData?.filePathSml, //縮圖
      filePath: rowData?.filePath, //原圖
      materialType: rowData?.materialType, //原燃物料類別
      materialCode: rowData?.materialCode, //原燃物料代碼
      isBiomass: rowData?.isBiomass, //是否屬生質能源
      isCHP: rowData?.isCHP, //是否屬汽電共生
      customerNo: rowData?.customerNo, //電號
      // 類四
      sourceName: rowData?.sourceName,
    });

    setValue("classSeq", {
      className: classGroup?.className,
      classNo: rowData?.classNo,
      classSeq: classGroup?.classSeq,
    });
    setValue("isCalculate", rowData?.isCalculate === "Y" ? true : false, {
      shouldValidate: true,
    });
    setValue("isBiomass", rowData?.isBiomass === "Y" ? true : false, {
      shouldValidate: true,
    });
    setValue("isCHP", rowData?.isCHP === "Y" ? true : false, { shouldValidate: true });
    setValue("materialType", rowData?.materialType, { shouldValidate: true });
    setValue("materialCode", rowData?.materialCode, { shouldValidate: true });
    setValue("customerNo", rowData?.customerNo, { shouldValidate: true });
    setValue("identifyDetailList", rowData?.identifyDetailList || []);
    setValue("planPlaceSeq", rowData?.planPlaceSeq, { shouldValidate: true });
    setValue("placeDeviceSeq", rowData?.placeDeviceSeq, { shouldValidate: true });
    setValue("quantity", rowData?.quantity, { shouldValidate: true });
    setValue("dept", rowData?.dept, { shouldValidate: true });
    setValue("location", rowData?.location, { shouldValidate: true });
    // 新增類四參數
    let hasCustom = rowData?.hasCustom === null ? false : rowData?.hasCustom === "Y" ? true : false;

    setValue("isCusCoefficent", hasCustom); //自定義係數
    if (rowData?.classNo === "4") {
      setValue("cusSourceSeq", rowData?.sourceSeq);
    }
    if (hasCustom) {
      setValue("customRate1", rowData?.customRate1, { shouldValidate: true });
      setValue("customRateSource", rowData?.customRateSource, { shouldValidate: true });
      setValue("customRateType", rowData?.customRateType, { shouldValidate: true });
      setValue("customEmission", rowData?.customEmission, { shouldValidate: true });
      setValue("customDesc", rowData?.customDesc, { shouldValidate: true });
      setValue("customFileName", rowData?.customFileName);
      setValue("customFileExtension", rowData?.customFileExtension);
    }
  };

  // 複製
  const copyToClipboard = (data) => {
    const textToCopy = data;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        handleSuccessMessage(dispatch, t("copy") + " " + t("message.success"));
      })
      .catch((error) => {});
  };

  const toggleTableOpen = (key) => {
    //control table expand
    setTableOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 下載模板及資料
  const downloadCarbonTemplate = () => {
    checkLoading(loading);
    const reqBody = {
      planSn: itemObj?.planSn,
      companySeq: itemObj?.companySeq,
    };
    downloadCarbonTemplateExcel(reqBody)
      .then((res) => {
        if (res?.status === 200) {
          handleFileDownloadRes(
            res,
            `${itemObj?.year}_${itemObj?.displayCause}_${itemObj?.companyName}${
              itemObj?.placeName ? `_${itemObj?.placeName}` : ""
            }_template.xlsx`,
          );
        } else {
          AlertService.ok(res?.data?.ResHeader?.rtnCode, res?.data?.ResHeader?.rtnMsg);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 匯入資料
  const importCarbonTemplate = () => {
    let formData = new FormData();
    formData.append("planSn", itemObj?.planSn);
    formData.append("companySeq", itemObj?.companySeq);
    formData.append("file", importFile);
    checkLoading(loading);
    importCarbonTemplateExcel(formData)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.import") + " " + t("message.success"));
          axios_queryPlanClassList("edit");
        }
      })
      .finally(() => {
        setPreviewUrl(null);
        setImportFileInfo(null);
        setImportFile({});
        setImportFile(null);
        setImportPopup(false);
        checkLoading(loading, false);
      });
  };

  // 每次開啟選擇檔案觸發onChange事件
  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    const imgSize = 5000000; // 20MB

    if (file.size >= imgSize) {
      AlertService.ok("Error", `Size over 5MB`);
      setPreviewUrl(null);
      setImportFileInfo(null);
      setImportFile({});
      if (importfileInputRef.current) {
        importfileInputRef.current.value = null;
      }
    } else {
      const objectURL = URL.createObjectURL(file);
      setPreviewUrl(objectURL);
      setImportFileInfo(file);
      setImportFile(file);
    }
  };

  // 刪除圖片
  const handleDeleteImg = () => {
    if (importfileInputRef?.current?.value !== undefined) {
      importfileInputRef.current.value = null;
      setPreviewUrl(null);
      setImportFileInfo(null);
      setImportFile({});
    }
  };

  // 刪除批次
  const batchDelete = () => {
    checkLoading(loading);
    const sortResult = Object.entries(selectValue)
      .filter(([classNo, data]) => data?.length > 0) // 空陣列跳過
      .flatMap(([classNo, data]) => data.map((item) => item.identifySeq));
    deleteIdentifyItem({ identifySeq: sortResult })
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
          axios_queryPlanClassList("edit");
        }
      })
      .finally((fin) => {
        setBatchAction("");
        setSelectValue({});
        setBatchDelPopup(false);
        checkLoading(loading, false);
      });
  };

  // 處理刪除
  const handleDataDel = (classNo, newData) => {
    setSelectValue((prev) => ({
      ...prev,
      [classNo]: newData,
    }));
  };

  // 處理排序
  const handleDataReordered = (classNo, newData) => {
    setReorderedDataMap((prev) => ({
      ...prev,
      [classNo]: newData,
    }));
  };

  const handleSort = () => {
    const sortResult = Object.entries(reorderedDataMap)
      .map(([classNo, data]) => {
        return data.map((item, index) => ({
          identifySeq: item.identifySeq,
          sort: index,
        }));
      })
      .flat();
    const reqBody = {
      planSn: itemObj?.planSn,
      sortList: sortResult,
    };
    sortIdentificationItem(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.sort") + " " + t("message.success"));
          axios_queryPlanClassList("edit");
        }
      })
      .finally(() => {
        setReorderedDataMap({});
      });
  };

  // 批次上傳附件
  const handleBatchFileChange = (e, rowData) => {
    const file = e?.target?.files?.[0];
    const imgSize = 5000000; // 20MB

    if (!file) {
      return;
    }

    checkLoading(loading);
    if (file.size >= imgSize) {
      AlertService.ok("Error", `Size over 5MB`);
      checkLoading(loading, false);
    } else {
      const formData = new FormData();

      //call 共用上傳API API好了開啟這裡
      const fileExtension = splitFileExtension(file?.name);
      const fileName = splitFileName(file?.name);
      formData.append("fileName", fileName);
      formData.append("filePath", "cfv_plan_emission_identify/");
      formData.append("fileCateCode", "cfv_plan_emission_identify");
      formData.append("refId", rowData.identifySeq);
      formData.append("attachFile", file);
      formData.append("fileExtension", fileExtension);
      // 加上 companySeq
      formData.append("companySeq", itemObj?.companySeq);
      uploadMultipartFile(formData)
        .then((res) => {
          if (checkDpSuccess(res)) {
            handleSuccessMessage(dispatch, t("button.upload") + " " + t("message.success"));
            axios_queryPlanClassList("edit");
          }
        })
        .finally((fin) => {
          checkLoading(loading, false);
        });
    }
  };

  // 批次刪除附件
  const handleBatchDeleteImg = (fileId) => {
    checkLoading(loading);
    deleteMultipartFile({ fileId })
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
          axios_queryPlanClassList("edit");
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const batcActionConent = () => {
    switch (batchAction) {
      case "delete":
        return <span className="font-bold">{`${t("button.batch")}${t("button.delete")}`}</span>;
      case "sort":
        return <span className="font-bold">{`${t("button.edit")}${t("button.sort")}`}</span>;
      case "upload":
        return (
          <span className="font-bold">{`${t("button.batch")}${t("button.edit")}${t("attached_picture")}/${t(
            "attachments",
          )}`}</span>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className="flex gap-2 align-items-center justify-content-center w-full"
        style={{ transform: "translateY(-1rem)" }}
      >
        {/* 新增 */}
        {batchAction === "" && (
          <>
            <Button
              icon="pi pi-plus"
              disabled={!isEditable}
              label={t("button.add")}
              onClick={() => {
                addItem();
              }}
              type="button"
              size="small"
            />
            <Controller
              name={`queryPlanPlaceSeq`}
              control={control}
              render={({ field, fieldState }) => (
                <Dropdown
                  showClear
                  optionLabel="placeName"
                  optionValue="planPlaceSeq"
                  value={field.value}
                  options={companyPlaceList}
                  placeholder={t("plz_chs_all")}
                  className={`flex-1 ` + classNames({ "p-invalid": fieldState.error })}
                  onChange={(e) => {
                    field.onChange(e.value);
                    setValue("planPlaceSeq", e.value);
                    axios_queryPlanClassList("", {
                      planSn: itemObj?.planSn,
                      class: getClassByCriterion(itemObj?.criterion),
                      planPlaceSeq: e.value,
                    });
                  }}
                />
              )}
            />
          </>
        )}
        {batcActionConent()}

        {batchAction === "" && (
          <div className="ml-auto">
            <Menu model={items} popup ref={menu} />
            <Button
              className="p-button-icon-only"
              onClick={(event) => menu.current.toggle(event)}
              icon="pi pi-ellipsis-h"
              aria-controls="popup_menu"
              rounded
              text
              aria-haspopup
            />
          </div>
        )}

        {batchAction !== "" && (
          <div className="ml-auto">
            {/* 刪除 */}
            {batchAction === "delete" && (
              <Button
                disabled={!isEditable || Object.keys(selectValue).length === 0}
                className="p-button-icon-only"
                icon="pi pi-trash"
                onClick={() => {
                  handleDataDel();
                  setBatchDelPopup(true);
                }}
                tooltip={t("button.delete")}
                tooltipOptions={{ position: "top" }}
                type="button"
                size="small"
                severity="danger"
                rounded
                text
              />
            )}
            {/* 存檔 */}
            {batchAction !== "delete" && batchAction !== "upload" && (
              <Button
                disabled={!isEditable}
                className="p-button-icon-only"
                icon="pi pi-save"
                onClick={() => {
                  if (batchAction === "sort") {
                    handleSort();
                  }
                  setBatchAction("");
                }}
                tooltip={t("button.save")}
                tooltipOptions={{ position: "top" }}
                type="button"
                rounded
                text
                size="small"
              />
            )}
            {/* 取消 */}
            <Button
              disabled={!isEditable}
              className="p-button-icon-only"
              icon="pi pi-times"
              onClick={() => {
                setBatchAction("");
                setSelectValue({});
              }}
              tooltip={t("button.cancel")}
              tooltipOptions={{ position: "top" }}
              type="button"
              rounded
              text
              size="small"
            />
          </div>
        )}
      </div>

      {itemObj?.importanceIdentification === "Y" && importantList?.length > 0 && (
        <div className="flex align-items-center my-2">
          <Tooltip target=".pi-info-circle" />
          <span>
            {" "}
            <i className="pi pi-info-circle ml-1 text-orange-500" data-pr-tooltip={importantList}>
              {" "}
              {t("unestablishEmissItem")}
            </i>{" "}
          </span>
        </div>
      )}

      <div className="flex flex-column gap-3">
        {data?.map((e, index) => {
          return (
            <Fragment key={e?.classSeq || index}>
              {e?.emissionList?.length > 0 && (
                <div>
                  <CategoryList
                    register={register}
                    currentIndex={index}
                    handleSuccessMessage={handleSuccessMessage}
                    dispatch={dispatch}
                    header={e?.displayClassName}
                    sortableElement={sortableElement?.current?.[index]}
                    tableOpen={!!tableOpen[e?.className]}
                    setTableOpen={() => toggleTableOpen(e?.className)}
                    data={e?.emissionList}
                    classSeq={data[index]}
                    editItem={editItem}
                    isEditable={isEditable}
                    axios_queryPlanClassList={axios_queryPlanClassList}
                    classNo={e?.classNo}
                    importantList={importantList}
                    batchAction={batchAction}
                    selectValue={selectValue}
                    setSelectValue={setSelectValue}
                    dataDel={(rowData) => handleDataDel(e.displayClassNo, rowData)}
                    dataReordered={(newData) => handleDataReordered(e.displayClassNo, newData)}
                    handleDeleteImg={(fileId) => handleBatchDeleteImg(fileId)}
                    handleFileChange={(e, data) => handleBatchFileChange(e, data)}
                    approveFlag={approveFlag}
                    companySeq={itemObj?.companySeq}
                  />
                </div>
              )}
            </Fragment>
          );
        })}

        {/* CategoryList = ({header,dataReordered,data,sortableElement,tableOpen,setTableOpen}) */}
        {/* {data?.map((e) => CategoryList({ header: e?.className, data: e?.emissionList }))} */}
      </div>
      {/* 新增 */}
      <PrimeDialog
        scrollByBody={true}
        //   disabledConfirm={!isValid}
        disabledConfirm={
          (actionStatus === "edit" && !isEditable) ||
          (actionStatus === "add" ? !(isValid && isDirty) : !isValid) ||
          actionStatus === "detail"
        }
        title={
          <div>
            <label>
              {t(`button.${actionStatus}`)}
              {actionStatus !== "add" && (
                <Button
                  size="small"
                  type="button"
                  tabIndex={-1}
                  style={{ alignItems: "flex-start", padding: "0px" }}
                  tooltipOptions={{ position: "right" }}
                  tooltip={`${t("button.copy")} ${editObj?.identifySeq}`}
                  text
                  onClick={() => {
                    copyToClipboard(editObj?.identifySeq);
                  }}
                  icon="pi pi-copy"
                />
              )}
            </label>
          </div>
        }
        onClose={() => {
          handleCloseAddDialog();
        }}
        onConfirm={() => {
          dialogSubmit();
        }}
        visible={addDialog}
        size="lg"
        bodyTemplate={
          <AddItem
            trigger={trigger}
            fileInputRef={fileInputRef}
            customFileInputRef={customFileInputRef}
            clearErrors={clearErrors}
            getValues={getValues}
            setValue={setValue}
            criterion={itemObj?.criterion}
            handleSubmit={handleSubmit}
            control={control}
            register={register}
            errors={errors}
            itemObj={itemObj}
            actionStatus={actionStatus}
            editObj={editObj}
            watch={watch}
            isEditable={isEditable}
            gwpVersion={itemObj.displayGwp}
            companyPlaceList={companyPlaceList}
            boudaryDeviceList={boudaryDeviceList}
            planType={itemObj?.planType}
          />
        }
      />

      {/* 確認Dialog */}
      <PrimeDialog
        visible={previewDialog}
        cancelLabel="button.quitSave"
        confirmLabel="button.inheritData"
        deleteLabel="button.deleteData"
        isShowDelBtn={true}
        onDelete={() => {
          confirmPreview("Y");
        }}
        onClose={() => {
          setPreviewDialog(false);
          // setAddDialog(false);
        }}
        onConfirm={() => {
          confirmPreview("N");
        }}
        title={``}
        loading={confirmLoad}
        bodyTemplate={<>{t("changeIdentify")}</>}
      />

      {/* 匯入資料 */}
      <PrimeDialog
        visible={importPopup}
        onClose={() => {
          setImportPopup(false);
          setPreviewUrl(null);
          setImportFileInfo(null);
          setImportFile({});
          setImportFile(null);
        }}
        onConfirm={() => {
          importCarbonTemplate();
        }}
        title={t("button.importCarbonFootprintTemplate")}
        loading={confirmLoad}
        bodyTemplate={
          <div className="border-round p-3">
            <i className="pi pi-info-circle mb-3 text-orange-500" data-pr-tooltip={importantList}>
              {" "}
              {t("importContent")}
            </i>
            <FormInputFile
              register={register}
              errors={errors}
              acceptFileType=".xlsx"
              id="file"
              name="file"
              disabled={!isEditable}
              fileChange={(e) => {
                handleFileChange(e);
              }}
              fileInputRef={importfileInputRef}
              limitSize="20MB"
            />
            <ImagePDFComponent
              wrapperClass="mt-2"
              file={importFileInfo}
              previewUrl={previewUrl}
              disabled={!isEditable}
              deleteImg={handleDeleteImg}
            />
          </div>
        }
      />

      {/* 批次刪除 */}
      <PrimeDialog
        visible={batchDelPopup}
        isShowConfBtn={false}
        isShowDelBtn={true}
        onDelete={() => {
          batchDelete();
        }}
        onClose={() => {
          setBatchDelPopup(false);
        }}
        title={`${t("button.batch")}${t("button.delete")}`}
        bodyTemplate={<>{t("batchDeleteContent")}</>}
      />
    </>
  );
};

// const CategoryList = ({ header, data }) => {
//     const dataReordered = useRef(data);
//     const sortableElement = useRef(null);
//     const [tableOpen ,setTableOpen] = useState(true);
//     // useEffect(() => {
//     //     new Sortable(sortableElement.current, {
//     //         animation: 150,
//     //         onEnd: (e) => {
//     //             const { oldIndex, newIndex } = e;
//     //             const dataCopied = dataReordered.current.concat();
//     //             const item = dataCopied.splice(oldIndex, 1)[0];

//     //             dataCopied.splice(newIndex, 0, item)
//     //             dataReordered.current = dataCopied;

//     //             console.log(dataReordered.current);
//     //         }
//     //     });

//     // }, [])

//     return <Fragment key={header}>
//         <div className={`custom-collapse-table ${tableOpen?'active':''}`}>
//             <h4 className="custom-collapse-table-header">
//                 {header}
//                 <Button icon={`pi ${tableOpen?'pi-angle-down':'pi-angle-left'}`} rounded text severity="secondary" onClick={()=>{setTableOpen(!tableOpen)}} />
//             </h4>
//             <div className="custom-collapse-table-body">
//                 <div className="sort-table-theader">
//                     <div className="sort-table-theader-tr">
//                         <div className="sort-table-theader-td">
//                             <span>項次</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>排放型式</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>設備</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>排放源</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>數量</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>列入計算</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>附圖</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>備註</span>
//                         </div>
//                         <div className="sort-table-theader-td">
//                             <span>動作</span>
//                         </div>
//                     </div>
//                 </div>
//                 <div className="sort-table-tbody" ref={sortableElement} >
//                     {data?.map((item, i) =>
//                         <div className="sort-table-tbody-tr" key={i}>
//                             {/* 項次 */}
//                             <div className="sort-table-tbody-td td-col-sort">
//                                 <span>{item?.sort}</span>
//                             </div>
//                             {/* 排放型式 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">排放型式</span>
//                                 <span>{item?.typeName}</span>
//                             </div>
//                             {/* 設備 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">設備</span>
//                                 <span>{item?.deviceName}</span>
//                             </div>
//                             {/* 排放源 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">排放源</span>
//                                 <span>{item?.sourceName}</span>
//                             </div>
//                             {/* 數量 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header" >數量</span>
//                                 <span>{item?.quantity}</span>
//                             </div>
//                             {/* 列入計算 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">列入計算</span>
//                                 <span>{item?.isCalculate}</span>
//                             </div>
//                             {/* 附圖 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">附圖</span>
//                                 <span>{item?.devicePicPath}</span>
//                             </div>
//                             {/* 備註 */}
//                             <div className="sort-table-tbody-td">
//                                 <span className="mobile-header">備註</span>
//                                 <span>{item?.remark}</span>
//                             </div>
//                             {/* 動作 */}
//                             <div className="sort-table-tbody-td td-col-action">
//                                 {/* 動作 */}
//                                 <Button className="p-button-icon-only" rounded text severity="secondary">
//                                     <FontAwesomeIcon icon="fa-solid fa-edit" />
//                                 </Button>
//                                 <Button className="p-button-icon-only" rounded text severity="secondary">
//                                     <FontAwesomeIcon icon="fa-solid fa-copy" />
//                                 </Button>
//                                 <Button className="p-button-icon-only" rounded text severity="danger">
//                                     <FontAwesomeIcon icon="fa-solid fa-trash-can" />
//                                 </Button>
//                             </div>
//                         </div>)}
//                 </div>
//             </div>
//         </div>
//     </Fragment>

// }

export default Identification;
